/**
 * Translations for i18n. Keys use dot notation: "nav.portfolio", "common.save"
 * English is the source; Hebrew values may be partial (fallback to en).
 */
export type Locale = "en" | "he"

export const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Common
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.next": "Next",
    "common.back": "Back",
    "common.complete": "Complete",
    "common.loading": "Loading",
    "common.error": "Error",
    "common.retry": "Retry",
    "common.signOut": "Sign Out",
    "common.close": "Close",
    // Nav
    "nav.portfolio": "Portfolio",
    "nav.overview": "Overview",
    "nav.transactions": "Transactions",
    "nav.risk": "Risk & Performance",
    "nav.visualizations": "Visualizations",
    "nav.research": "Research",
    "nav.news": "News",
    "nav.analystRatings": "Analyst Ratings",
    "nav.validatedPicks": "Validated Picks",
    "nav.transparentAnalysis": "Transparent Analysis",
    "nav.aiAssistant": "AI Assistant",
    "nav.social": "Social & Competition",
    "nav.leaderboard": "Leaderboard",
    "nav.leagues": "Leagues",
    "nav.achievements": "Achievements",
    "nav.reports": "Reports",
    "nav.taxReporting": "Tax Reporting",
    "nav.dividends": "Dividends",
    "nav.tools": "Tools",
    "nav.learn": "Learn",
    "nav.puzzles": "Puzzles",
    "nav.progress": "Progress",
    "nav.investSmarter": "Invest Smarter",
    "nav.alerts": "Alerts",
    "nav.settings": "Settings",
    "nav.admin": "Admin",
    "nav.dashboard": "Dashboard",
    // Settings
    "settings.title": "Settings",
    "settings.subtitle": "Manage your account and preferences",
    "settings.profile": "Profile",
    "settings.achievements": "Achievements",
    "settings.preferences": "Preferences",
    "settings.export": "Export",
    "settings.activity": "Activity",
    "settings.profileInfo": "Profile Information",
    "settings.updateProfile": "Update your profile details",
    "settings.theme": "Theme",
    "settings.themeDescription": "Choose your preferred theme",
    "settings.light": "Light",
    "settings.dark": "Dark",
    "settings.system": "System",
    "settings.language": "Language",
    "settings.languageDescription": "Choose your preferred language",
    "settings.english": "English",
    "settings.hebrew": "עברית",
    "settings.notifications": "Notifications",
    "settings.manageAlerts": "Manage your alert preferences",
    "settings.dangerZone": "Danger Zone",
    "settings.logout": "Logout",
    // Onboarding
    "onboarding.welcome": "Welcome!",
    "onboarding.welcomeDesc": "Let's set up your investment portfolio",
    "onboarding.language": "Preferred language",
    "onboarding.languageDesc": "Choose the language for the app",
    "onboarding.username": "Username",
    "onboarding.enterName": "Enter your name",
    "onboarding.email": "Email",
    "onboarding.emailHint": "Used for alerts and notifications",
    "onboarding.investmentPrefs": "Investment Preferences",
    "onboarding.personalize": "Help us personalize your experience",
    "onboarding.riskTolerance": "Risk Tolerance",
    "onboarding.investmentFocus": "Investment Focus",
    "onboarding.addHoldings": "Add Your Holdings",
    "onboarding.importMethod": "How would you like to add your portfolio?",
    "onboarding.step": "Step",
    "onboarding.of": "of",
    "onboarding.portfolioMethod": "Portfolio Import Method",
    "onboarding.manualEntry": "Manual Entry",
    "onboarding.manualEntryDesc": "Add stocks one by one",
    "onboarding.excelUpload": "Excel Upload",
    "onboarding.excelUploadDesc": "Import from CSV/Excel",
    "onboarding.screenshot": "Screenshot",
    "onboarding.screenshotDesc": "AI extraction from image",
    "onboarding.conservative": "Conservative (Low Risk)",
    "onboarding.moderate": "Moderate (Medium Risk)",
    "onboarding.aggressive": "Aggressive (High Risk)",
    "onboarding.tech": "Technology",
    "onboarding.crypto": "Cryptocurrency",
    "onboarding.diversified": "Diversified",
    "onboarding.value": "Value Investing",
    // Login / Auth
    "auth.signIn": "Sign In",
    "auth.signUp": "Sign Up",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.forgotPassword": "Forgot password?",
    "auth.noAccount": "Don't have an account?",
    "auth.hasAccount": "Already have an account?",
    "auth.continueWithGoogle": "Continue with Google",
    "auth.welcome": "Welcome",
    "auth.welcomeBack": "Welcome back",
    "auth.createAccount": "Create account",
    "auth.startJourney": "Start your investment journey with Igani Capital",
    "auth.confirmPassword": "Confirm password",
    "auth.createPasswordPlaceholder": "Create a password (min 8 characters)",
    "auth.confirmPasswordPlaceholder": "Confirm your password",
    "auth.hasAccountSignIn": "Already have an account? Sign in",
    "auth.creatingAccount": "Creating account...",
    "auth.redirecting": "Redirecting...",
    "auth.signingIn": "Signing in...",
    // Landing
    "landing.signIn": "Sign In",
    "landing.getStarted": "Get Started",
    "landing.tagline": "Your intelligent partner for advanced investment analysis and portfolio optimization.",
    "landing.subTagline": "Track stocks and crypto in one place. Compete in leagues, get AI-powered insights, and manage risk with transparent analytics.",
    "landing.joinLeading": "Join leading investors using Igani Capital",
    // Learn
    "learn.title": "Learn",
    "learn.description": "Structured lessons on investing basics and strategies. Education only—not financial advice.",
    "learn.lessons": "Lessons",
    "learn.continueWhere": "Continue where you left off",
    "learn.recommendedNext": "Your recommended next lesson",
    "learn.openLesson": "Open lesson",
    "learn.dailyPuzzle": "Daily puzzle",
    "learn.dailyPuzzleDesc": "Test your chart reading with a daily puzzle",
    "learn.goToPuzzles": "Go to puzzles",
    "learn.sessionExpired": "Session expired. Sign in again to sync your progress.",
    "learn.signIn": "Sign in",
    "learn.educationDisclaimer": "Content is for education only. Not personal financial advice. Past performance does not guarantee future results.",
    "learn.backToLearn": "Back to Learn",
    "learn.lessonNotFound": "Lesson not found.",
    "learn.lessonComplete": "Lesson complete!",
    "learn.notFinancialAdvice": "Education only. Not financial advice.",
    "learn.concept": "Concept",
    "learn.whyItMatters": "Why it matters",
    "learn.keyTakeaways": "Key takeaways",
    "learn.commonMistakes": "Common mistakes",
    "learn.selectAnswerToComplete": "Select an answer to complete",
    "learn.markComplete": "Mark complete",
    "learn.completed": "Completed",
    "learn.practiceThisLesson": "Practice this lesson",
    "learn.path.beginner.title": "Beginner",
    "learn.path.beginner.description": "Foundations of investing: risk, diversification, and long-term thinking.",
    "learn.path.intermediate.title": "Intermediate",
    "learn.path.intermediate.description": "Strategies and discipline: value, momentum, rebalancing, and fees.",
    "learn.path.advanced.title": "Advanced",
    "learn.path.advanced.description": "Position sizing, stop-loss vs targets, and charting tools (education only).",
    "learn.lesson.risk-reward.title": "Risk and Reward",
    "learn.lesson.risk-reward.summary": "Understanding the relationship between risk and potential return is the foundation of investing.",
    "learn.lesson.diversification.title": "Diversification",
    "learn.lesson.diversification.summary": "Spreading your investments across many assets can reduce the impact of any single one failing.",
    "learn.lesson.indexing-basics.title": "Indexing Basics",
    "learn.lesson.indexing-basics.summary": "Index funds track a market index and offer broad exposure at low cost.",
    "learn.lesson.dca.title": "Dollar-Cost Averaging (DCA)",
    "learn.lesson.dca.summary": "Investing a fixed amount on a schedule can reduce the impact of timing and emotions.",
    "learn.lesson.time-horizons.title": "Time Horizons",
    "learn.lesson.time-horizons.summary": "How long you plan to invest should shape how much risk you take.",
    "learn.lesson.value-investing.title": "Value Investing Basics",
    "learn.lesson.value-investing.summary": "Buying assets that appear undervalued relative to their fundamentals.",
    "learn.lesson.rebalancing.title": "Rebalancing",
    "learn.lesson.rebalancing.summary": "Periodically adjusting your portfolio back to target weights can manage risk and enforce discipline.",
    "learn.lesson.fibonacci-tool.title": "Fibonacci Retracement (Education)",
    "learn.lesson.fibonacci-tool.summary": "A common technical analysis tool used to identify potential support/resistance levels. Not a guarantee of future price.",
    // Puzzles
    "puzzles.title": "Puzzles",
    "puzzles.description": "Chart-reading challenges. Build your rating and streaks. Education only—not trading advice.",
    "puzzles.dailyPuzzle": "Daily puzzle",
    "puzzles.completedToday": "Completed today",
    "puzzles.oneNewDaily": "One new puzzle every day",
    "puzzles.playAgain": "Play again",
    "puzzles.playTodays": "Play today's puzzle",
    "puzzles.load": "Load…",
    "puzzles.reviewTodays": "Review today's puzzle",
    "puzzles.streak": "Streak",
    "puzzles.daysInRow": "days in a row",
    "puzzles.rating": "Rating",
    "puzzles.ratingDesc": "Puzzle rating (Elo-style)",
    "puzzles.couldNotGetSession": "Could not get session. Please try again.",
    "puzzles.sessionExpiredProgress": "Session expired. Sign in again to see your progress and recently played.",
    "puzzles.somethingWentWrong": "Something went wrong loading progress.",
    "puzzles.recentlyPlayed": "Recently played",
    "puzzles.jumpBackReview": "Jump back or review mistakes",
    "puzzles.practice": "Practice",
    "puzzles.filterAndSort": "Filter and sort puzzles",
    "puzzles.puzzleSets": "Puzzle sets",
    "puzzles.mistakeReview": "Mistake review",
    "puzzles.recommendedForYou": "Recommended for you",
    "puzzles.allPuzzles": "All puzzles",
    "puzzles.noPuzzlesMatch": "No puzzles match the current filters.",
    "puzzles.educationDisclaimer": "Puzzles are for education only. Not financial or trading advice. No real money at risk.",
    // Leaderboard
    "leaderboard.title": "World Leaderboard",
    "leaderboard.description": "Top learners by XP. Complete lessons and puzzles to climb.",
    "leaderboard.yourProgress": "Your progress",
    "leaderboard.keepGoing": "Keep going to reach the top 20!",
    "leaderboard.top20": "Top 20 by XP",
    "leaderboard.rankDescription": "Rank, display name, and total experience points",
    "leaderboard.emptyMessage": "No entries yet. Complete lessons and puzzles to earn XP and appear on the board!",
    // Achievements
    "achievements.title": "Achievements",
    "achievements.subtitle": "Your collection and progress",
    "achievements.allRarity": "All rarities",
    "achievements.allCategories": "All categories",
    "achievements.searchPlaceholder": "Search achievements…",
    "achievements.empty": "No achievements match your filters.",
    "achievements.unlocked": "Unlocked",
    "achievements.locked": "Locked",
    "achievements.collectAndClimb": "Collect achievements and climb the ranks",
    "achievements.totalXp": "Total XP",
    "achievements.completion": "Completion",
    "achievements.rarityBreakdown": "Rarity Breakdown",
    "achievements.noFound": "No achievements found",
    "achievements.tryAdjustingFilters": "Try adjusting your filters",
    "achievements.noUnlockedYet": "No achievements unlocked yet",
    "achievements.startCompeting": "Start competing in groups to unlock achievements!",
    "leagues.loading": "Loading leagues…",
    "leagues.backToDashboard": "Back to Dashboard",
    "leagues.noLeaguesYet": "No leagues yet",
    "leagues.createFirstLeague": "Create your first league to start competing with friends",
    // Alerts
    "alerts.title": "Alerts & Notifications",
    "alerts.subtitle": "Set up price alerts and monitor market activity",
    "alerts.activeAlerts": "Active Alerts",
    "alerts.triggeredToday": "Triggered Today",
    "alerts.upcomingEarnings": "Upcoming Earnings",
    "alerts.unusualActivity": "Unusual Activity",
    // Leagues
    "leagues.title": "Leagues",
    "leagues.subtitle": "Compete with friends and track performance",
    "leagues.createLeague": "Create League",
    "leagues.myLeagues": "My Leagues",
    "leagues.discover": "Discover",
    "leagues.leagueName": "League name",
    "leagues.leagueDescription": "Description (optional)",
    "leagues.create": "Create",
    "leagues.leagueCreated": "League created!",
    "leagues.inviteLink": "Invite link",
    "leagues.noLeagues": "You're not in any leagues yet.",
    "leagues.joinOrCreate": "Join with an invite link or create one.",
    // Transactions / Risk / Tax / etc (page titles and common phrases)
    "transactions.title": "Transactions",
    "risk.title": "Risk & Performance",
    "tax.title": "Tax Reporting",
    "dividends.title": "Dividends",
    "ratings.title": "Analyst Ratings",
    "ratings.subtitle": "Professional analyst recommendations for your holdings",
    "validated.title": "Validated Picks",
    "validated.pageTitle": "Analyst-Validated Recommendations",
    "validated.pageSubtitle": "Rule-based investment recommendations using strict validation algorithm",
    "transparent.title": "Transparent Analysis",
    "transparent.pageTitle": "Transparent Investment Analysis",
    "transparent.subtitle": "Comprehensive multi-source analysis with full transparency into our methodology",
    "aiAssistant.title": "AI Assistant",
    "aiAssistant.subtitle": "Get real-time answers about your portfolio, account details, and investment strategies",
    "aiAssistant.loading": "Loading AI Assistant...",
    "investSmarter.title": "Invest Smarter",
    "investSmarter.subtitle": "Learn, analyze, and optimize your investment strategy with AI-powered insights",
    "investSmarter.twelveStepSystem": "12 Step System",
    "decisions.title": "Decisions",
    "decisions.pageTitle": "Decision Support",
    "decisions.subtitle": "AI-powered recommendations and scenario analysis",
    "decisions.buySignals": "Buy Signals",
    "decisions.sellSignals": "Sell Signals",
    "decisions.rebalance": "Rebalance",
    "decisions.opportunities": "Opportunities",
    "visualizations.title": "Visualizations",
    "visualizations.pageTitle": "Interactive Visualizations",
    "visualizations.subtitle": "Explore your portfolio with interactive charts and tools",
    "visualizations.loading": "Loading visualizations...",
    "dashboard.overview": "Portfolio Overview",
    "dashboard.overviewSubtitle": "Track your investments and performance",
    "dashboard.noHoldings": "No Holdings Yet",
    "dashboard.addHolding": "Add holding",
    "dashboard.or": "or",
    "dashboard.stocks": "Stocks",
    "dashboard.etfs": "ETFs",
    "dashboard.crypto": "Crypto",
    "dashboard.showValues": "Show Values",
    "dashboard.totalPortfolio": "Total Portfolio",
    "dashboard.perAssetView": "Per-Asset View",
    "auth.signInFailed": "Sign in failed",
    "auth.failedGoogleSignIn": "Failed to sign in with Google",
    "auth.missingFields": "Missing fields",
    "auth.enterEmailPassword": "Please enter your email and password",
    "auth.useGoogleSignIn": "Use Google to sign in",
    "auth.googleAccountHint": "This account was created with Google. Sign in with Google to access it, or create a password in Settings after signing in.",
    "auth.wrongPassword": "Wrong email or password.",
    "auth.tooManyAttempts": "Too many attempts",
    "auth.tryAgainLater": "Please try again later or reset your password.",
    "auth.failedAddTransaction": "Failed to add transaction",
    "auth.signInToAdd": "Please sign in to add transactions",
    "auth.fillRequired": "Please fill in all required fields",
    "auth.sharesCostPositive": "Shares and cost per share must be greater than 0",
    "profile.title": "Profile",
    "admin.title": "Admin",
    "dashboard.title": "Dashboard",
    // Join flow
    "join.title": "Join League",
    "join.acceptInvite": "Accept invite",
    "join.invalidInvite": "Invalid or expired invite",
    // Auth placeholders
    "auth.emailPlaceholder": "Enter your email address",
    "auth.passwordPlaceholder": "Enter your password",
    "auth.signInSubHeader": "Sign in with your email and password, or use Google below",
    // Add transaction dialog
    "transaction.title": "Add Transaction",
    "transaction.description": "Buy or sell stocks, ETFs, or cryptocurrencies",
    "transaction.type": "Transaction Type",
    "transaction.buy": "Buy",
    "transaction.sell": "Sell",
    "transaction.symbol": "Symbol *",
    "transaction.symbolPlaceholder": "AAPL, BTC, SPYM",
    "transaction.shares": "Shares *",
    "transaction.sharesPlaceholder": "10",
    "transaction.costPerShare": "Cost per Share *",
    "transaction.costPlaceholder": "150.00",
    "transaction.date": "Date *",
    "transaction.commission": "Platform commission (%)",
    "transaction.commissionPlaceholder": "0",
    "transaction.commissionHintBuy": "Commission is added on top of trade value (increases your cost basis).",
    "transaction.commissionHintSell": "Commission is deducted from sale proceeds.",
    "transaction.commissionEg": "E.g. 2 for 2%.",
    "transaction.commissionType": "Commission type",
    "transaction.commissionPercent": "Percentage",
    "transaction.commissionFlat": "Flat rate",
    "transaction.commissionPercentLabel": "Commission (%)",
    "transaction.commissionFlatLabel": "Commission ($)",
    "transaction.commissionFlatPlaceholder": "0.00",
    "transaction.commissionFlatHint": "Fixed fee in dollars per trade (e.g. 5.95).",
    "transaction.notes": "Notes (optional)",
    "transaction.notesPlaceholder": "Add any notes about this transaction",
    "transaction.add": "Add Transaction",
    "transaction.adding": "Adding...",
    "transaction.added": "Transaction added",
    "transaction.invalidValues": "Invalid values",
    "transaction.notAuth": "Not authenticated",
    "transaction.cannotSell": "Cannot sell",
    "transaction.dontOwn": "You don't own",
    "transaction.insufficientShares": "Insufficient shares",
    "transaction.onlyOwn": "You only own",
    "transaction.sharesOf": "shares of",
    "transaction.at": "at",
    // Add asset dialog
    "asset.title": "Add Asset",
    "asset.description": "Pick Stocks, ETF, or Crypto, then search and select an asset.",
    "asset.whatToAdd": "What do you want to add?",
    "asset.searchStocks": "Search stocks",
    "asset.searchEtfs": "Search ETFs",
    "asset.searchCrypto": "Search crypto",
    "asset.placeholderStocks": "e.g. GOOGL, AAPL, MSFT",
    "asset.placeholderEtfs": "e.g. SPY, QQQ, VTI",
    "asset.placeholderCrypto": "e.g. Bitcoin, ETH, SOL",
    "asset.searching": "Searching…",
    "asset.loadingPrices": "Loading prices…",
    "asset.quantity": "Quantity",
    "asset.buyPrice": "Buy price (per unit)",
    "asset.buyDate": "Buy date (optional)",
    "asset.commissionHint": "If you paid more than the value of the shares (e.g. paid $100, got $98 worth), enter the commission % (e.g. 2).",
    "asset.summary": "Summary",
    "asset.costBasis": "Cost basis",
    "asset.currentValue": "Current value",
    "asset.unrealizedPnl": "Unrealized P&L ($)",
    "asset.unrealizedPnlPercent": "Unrealized P&L (%)",
    "asset.confirmSave": "Confirm & Save",
    "asset.saving": "Saving…",
    "asset.change": "Change",
    "asset.added": "Asset added",
    "asset.addedDesc": "added to portfolio.",
    "asset.failed": "Failed to add asset",
    // Alerts
    "alerts.yourAlerts": "Your Alerts",
    "alerts.noAlerts": "No active alerts",
    "alerts.noAlertsHint": "Create an alert to receive email notifications",
    "alerts.alertDeleted": "Alert deleted",
    "alerts.alertRemoved": "The alert has been removed.",
    "alerts.failedDelete": "Failed to delete alert",
    "alerts.createNew": "Create New Alert",
    "alerts.alertType": "Alert Type",
    "alerts.targetPrice": "Target Price",
    "alerts.ratingChange": "Rating Change",
    "alerts.majorNews": "Major News",
    "alerts.earnings": "Earnings",
    "alerts.symbol": "Symbol",
    "alerts.select": "Select...",
    "alerts.condition": "Condition",
    "alerts.value": "Value",
    "alerts.enterPrice": "Enter price",
    "alerts.createAlert": "Create Alert",
    "alerts.changeInSettings": "(Change in Settings)",
    "alerts.emailNotifications": "Email notifications sent to",
    "alerts.missingInfo": "Missing information",
    "alerts.missingInfoDesc": "Please select a symbol and ensure your email is configured.",
    "alerts.missingPrice": "Missing price",
    "alerts.missingPriceDesc": "Please enter a target price value.",
    "alerts.alertCreated": "Alert created",
    "alerts.alertCreatedDesc": "You'll receive email notifications when the condition is met.",
    "alerts.failedCreate": "Failed to create alert",
    "alerts.failedCreateDesc": "Failed to create alert. Please try again.",
    "alerts.triggered": "Triggered",
    "alerts.ratingChangeLabel": "rating change",
    "alerts.majorNewsLabel": "major news",
    "alerts.earningsLabel": "earnings",
    // League detail
    "league.today": "Today",
    "league.week": "Week",
    "league.month": "Month",
    "league.year": "Year",
    "league.dailySprint": "Daily sprint",
    "league.weeklyRound": "Weekly round",
    "league.monthlyStandings": "Monthly standings",
    "league.season": "Season",
    "league.noMembersYet": "No members yet.",
    "league.standingsLoading": "Standings loading.",
    "league.leading": "Leading",
    "league.copied": "Copied to clipboard",
    "league.couldNotCreateInvite": "Could not create invite",
    "league.failedCreateInvite": "Failed to create invite link",
    "league.updatedJustNow": "Updated just now",
    "league.updated1mAgo": "Updated 1m ago",
    "league.member": "Member",
    "league.owner": "Owner",
    "league.admin": "Admin",
    "league.league": "League",
    // Admin
    "admin.subtitle": "Platform overview and user management",
    "admin.accessDenied": "Access Denied",
    "admin.accessDeniedDesc": "You need admin access to view this page.",
    "admin.loading": "Loading admin dashboard and fetching prices...",
    "admin.dashboardTitle": "Admin Dashboard",
    "admin.accessRequired": "Admin access required",
    "admin.totalUsers": "Total Users",
    "admin.platformValue": "Platform Value",
    "admin.totalPnL": "Total P&L",
    "admin.totalHoldings": "Total Holdings",
    "admin.totalTransactions": "Total Transactions",
    "admin.cannotModifySelf": "Cannot modify your own admin status",
    "admin.cannotModifySelfDesc": "You cannot change your own admin status",
    "admin.adminGranted": "Admin status granted",
    "admin.adminRemoved": "Admin status removed",
    "admin.isNowAdmin": "is now an admin",
    "admin.noLongerAdmin": "is no longer an admin",
    "admin.failedUpdateAdmin": "Failed to update admin status",
    // User profile
    "profile.loading": "Loading profile…",
    "profile.userNotFound": "User not found",
    "profile.failedLoad": "Failed to load profile",
    "profile.backToLeagues": "Back to leagues",
    "profile.performanceAllocation": "Performance and allocation (percent only)",
    "profile.performance": "Performance",
    "profile.holdingsAllocation": "Holdings allocation",
    "profile.percentBySymbol": "Percent of portfolio by symbol. No dollar values shown.",
    "profile.noHoldingsData": "No holdings or allocation data.",
  },
  he: {
    "common.save": "שמור",
    "common.cancel": "ביטול",
    "common.next": "הבא",
    "common.back": "חזרה",
    "common.complete": "סיום",
    "common.loading": "טוען",
    "common.error": "שגיאה",
    "common.retry": "נסה שוב",
    "common.signOut": "התנתק",
    "common.close": "סגור",
    "nav.portfolio": "תיק השקעות",
    "nav.overview": "סקירה",
    "nav.transactions": "עסקאות",
    "nav.risk": "סיכון וביצועים",
    "nav.visualizations": "תצוגות",
    "nav.research": "מחקר",
    "nav.news": "חדשות",
    "nav.analystRatings": "דירוגי אנליסטים",
    "nav.validatedPicks": "בחירות מאומתות",
    "nav.transparentAnalysis": "ניתוח שקוף",
    "nav.aiAssistant": "עוזר AI",
    "nav.social": "חברתי ותחרות",
    "nav.leaderboard": "לוח מובילים",
    "nav.leagues": "ליגות",
    "nav.achievements": "הישגים",
    "nav.reports": "דוחות",
    "nav.taxReporting": "דיווח מס",
    "nav.dividends": "דיבידנדים",
    "nav.tools": "כלים",
    "nav.learn": "ללמוד",
    "nav.puzzles": "חידות",
    "nav.progress": "התקדמות",
    "nav.investSmarter": "השקע חכם",
    "nav.alerts": "התראות",
    "nav.settings": "הגדרות",
    "nav.admin": "מנהל",
    "nav.dashboard": "לוח בקרה",
    "settings.title": "הגדרות",
    "settings.subtitle": "נהל את החשבון וההעדפות שלך",
    "settings.profile": "פרופיל",
    "settings.achievements": "הישגים",
    "settings.preferences": "העדפות",
    "settings.export": "ייצוא",
    "settings.activity": "פעילות",
    "settings.profileInfo": "פרטי פרופיל",
    "settings.updateProfile": "עדכן את פרטי הפרופיל שלך",
    "settings.theme": "ערכת נושא",
    "settings.themeDescription": "בחר ערכת נושא",
    "settings.light": "בהיר",
    "settings.dark": "כהה",
    "settings.system": "מערכת",
    "settings.language": "שפה",
    "settings.languageDescription": "בחר את שפת הממשק",
    "settings.english": "English",
    "settings.hebrew": "עברית",
    "settings.notifications": "התראות",
    "settings.manageAlerts": "נהל העדפות התראות",
    "settings.dangerZone": "אזור מסוכן",
    "settings.logout": "התנתק",
    "onboarding.welcome": "ברוך הבא!",
    "onboarding.welcomeDesc": "בוא נגדיר את תיק ההשקעות שלך",
    "onboarding.language": "שפה מועדפת",
    "onboarding.languageDesc": "בחר את שפת האפליקציה",
    "onboarding.username": "שם משתמש",
    "onboarding.enterName": "הכנס את שמך",
    "onboarding.email": "אימייל",
    "onboarding.emailHint": "משמש להתראות ועדכונים",
    "onboarding.investmentPrefs": "העדפות השקעה",
    "onboarding.personalize": "עזור לנו להתאים את החוויה",
    "onboarding.riskTolerance": "סובלנות לסיכון",
    "onboarding.investmentFocus": "מיקוד השקעה",
    "onboarding.addHoldings": "הוסף נכסים",
    "onboarding.importMethod": "איך תרצה להוסיף את התיק?",
    "onboarding.step": "שלב",
    "onboarding.of": "מתוך",
    "onboarding.portfolioMethod": "אופן ייבוא התיק",
    "onboarding.manualEntry": "הזנה ידנית",
    "onboarding.manualEntryDesc": "הוסף מניות אחת אחת",
    "onboarding.excelUpload": "העלאת Excel",
    "onboarding.excelUploadDesc": "ייבוא מקובץ CSV/Excel",
    "onboarding.screenshot": "צילום מסך",
    "onboarding.screenshotDesc": "חילוץ באמצעות AI מתמונה",
    "onboarding.conservative": "שמרני (סיכון נמוך)",
    "onboarding.moderate": "בינוני (סיכון בינוני)",
    "onboarding.aggressive": "אגרסיבי (סיכון גבוה)",
    "onboarding.tech": "טכנולוגיה",
    "onboarding.crypto": "קריפטו",
    "onboarding.diversified": "מגוון",
    "onboarding.value": "השקעת ערך",
    "auth.signIn": "התחבר",
    "auth.signUp": "הרשם",
    "auth.email": "אימייל",
    "auth.password": "סיסמה",
    "auth.forgotPassword": "שכחת סיסמה?",
    "auth.noAccount": "אין לך חשבון?",
    "auth.hasAccount": "כבר יש לך חשבון?",
    "auth.continueWithGoogle": "המשך עם Google",
    "auth.welcome": "ברוך הבא",
    "auth.welcomeBack": "שוב ברוך הבא",
    "auth.createAccount": "צור חשבון",
    "auth.startJourney": "התחל את מסע ההשקעות שלך עם Igani Capital",
    "auth.confirmPassword": "אימות סיסמה",
    "auth.createPasswordPlaceholder": "צור סיסמה (מינימום 8 תווים)",
    "auth.confirmPasswordPlaceholder": "אשר את הסיסמה",
    "auth.hasAccountSignIn": "כבר יש לך חשבון? התחבר",
    "auth.creatingAccount": "יוצר חשבון...",
    "auth.redirecting": "מפנה...",
    "auth.signingIn": "מתחבר...",
    "landing.signIn": "התחבר",
    "landing.getStarted": "התחל",
    "landing.tagline": "השותף החכם שלך לניתוח השקעות מתקדם ואופטימיזציית תיק.",
    "landing.subTagline": "עקוב אחר מניות וקריפטו במקום אחד. התחרה בליגות, קבל תובנות AI ונהל סיכונים עם אנליטיקה שקופה.",
    "landing.joinLeading": "הצטרף למשקיעים מובילים עם Igani Capital",
    "learn.title": "ללמוד",
    "learn.description": "שיעורים מובנים על יסודות ואסטרטגיות השקעה. חינוך בלבד—לא ייעוץ פיננסי.",
    "learn.lessons": "שיעורים",
    "learn.continueWhere": "המשך מאיפה שהפסקת",
    "learn.recommendedNext": "השיעור המומלץ הבא שלך",
    "learn.openLesson": "פתח שיעור",
    "learn.dailyPuzzle": "חידון יומי",
    "learn.dailyPuzzleDesc": "בחן את קריאת הגרפים עם חידון יומי",
    "learn.goToPuzzles": "לחידות",
    "learn.sessionExpired": "ההפעלה פגה. התחבר שוב לסנכרון ההתקדמות.",
    "learn.signIn": "התחבר",
    "learn.educationDisclaimer": "התוכן לחינוך בלבד. לא ייעוץ פיננסי אישי. ביצועי עבר אינם מבטיחים תוצאות עתידיות.",
    "learn.backToLearn": "חזרה ללימוד",
    "learn.lessonNotFound": "השיעור לא נמצא.",
    "learn.lessonComplete": "השיעור הושלם!",
    "learn.notFinancialAdvice": "חינוך בלבד. לא ייעוץ פיננסי.",
    "learn.concept": "מושג",
    "learn.whyItMatters": "למה זה חשוב",
    "learn.keyTakeaways": "נקודות מרכזיות",
    "learn.commonMistakes": "טעויות נפוצות",
    "learn.selectAnswerToComplete": "בחר תשובה כדי להשלים",
    "learn.markComplete": "סמן כהושלם",
    "learn.completed": "הושלם",
    "learn.practiceThisLesson": "תרגל שיעור זה",
    "learn.path.beginner.title": "מתחיל",
    "learn.path.beginner.description": "יסודות ההשקעה: סיכון, פיזור וחשיבה ארוכת טווח.",
    "learn.path.intermediate.title": "בינוני",
    "learn.path.intermediate.description": "אסטרטגיות ומשמעת: ערך, מומנטום, איזון מחדש ועמלות.",
    "learn.path.advanced.title": "מתקדם",
    "learn.path.advanced.description": "גודל פוזיציה, stop-loss מול יעדים וכלי גרפים (חינוך בלבד).",
    "learn.lesson.risk-reward.title": "סיכון ותשואה",
    "learn.lesson.risk-reward.summary": "הבנת הקשר בין סיכון לתשואה פוטנציאלית היא יסוד ההשקעה.",
    "learn.lesson.diversification.title": "פיזור",
    "learn.lesson.diversification.summary": "פיזור ההשקעות על פני נכסים רבים מפחית את השפעת כישלון של נכס בודד.",
    "learn.lesson.indexing-basics.title": "יסודות האינדקס",
    "learn.lesson.indexing-basics.summary": "קרנות אינדקס עוקבות אחרי מדד שוק ומציעות חשיפה רחבה בעלות נמוכה.",
    "learn.lesson.dca.title": "ממוצע עלות דולרית (DCA)",
    "learn.lesson.dca.summary": "השקעה בסכום קבוע בתדירות קבועה מפחיתה את השפעת התזמון והרגשות.",
    "learn.lesson.time-horizons.title": "אופקי זמן",
    "learn.lesson.time-horizons.summary": "משך זמן ההשקעה המתוכנן צריך להשפיע על מידת הסיכון שתקח.",
    "learn.lesson.value-investing.title": "יסודות השקעת ערך",
    "learn.lesson.value-investing.summary": "רכישת נכסים שנראים undervalued ביחס ליסודות.",
    "learn.lesson.rebalancing.title": "איזון מחדש",
    "learn.lesson.rebalancing.summary": "התאמת התיק מחדש למשקלי היעד מנהלת סיכון ומחזקת משמעת.",
    "learn.lesson.fibonacci-tool.title": "רמות פיבונאצ'י (חינוך)",
    "learn.lesson.fibonacci-tool.summary": "כלי ניתוח טכני נפוץ לזיהוי רמות תמיכה/התנגדות. לא ערובה למחיר עתידי.",
    "puzzles.puzzle.p-trend-1.title": "זהה את המגמה",
    "puzzles.puzzle.p-trend-1.prompt": "קרא את הגרף: מה המגמה הדומיננטית? גבה בשני ראיות לפחות משיאים ומדפים.",
    "puzzles.puzzle.p-trend-1.explanation": "המחיר יוצר שורת שיאים עולים ו־מדפים עולים. המבנה מגדיר מגמת עלייה.",
    "puzzles.puzzle.p-mcq-1.title": "מה קרה אחר כך?",
    "puzzles.puzzle.p-mcq-1.prompt": "אחרי הנר האחרון, איזו תוצאה הכי מתאימה למבנה? בחר מה שהגרף תומך בו—לא ניחוש.",
    "puzzles.puzzle.p-mcq-1.explanation": "המבנה מראה שיאים עולים ומדפים עולים (מגמת עלייה). המשך המגמה אומר מחיר שעושה שיא חדש.",
    "puzzles.puzzle.p-mcq-1.option.0": "המחיר ירד חדות מתחת ל־מדף האחרון",
    "puzzles.puzzle.p-mcq-1.option.1": "המחיר נע בצד בטווח צר",
    "puzzles.puzzle.p-mcq-1.option.2": "המחיר עלה עוד לשיא חדש",
    "puzzles.puzzle.p-mcq-1.option.3": "המחיר נסוג לסגירה הקודמת ואז המשיך לעלות",
    "puzzles.puzzle.p-mcq-1.option.4": "המחיר עשה שיא גבוה יותר אבל נסגר ליד הפתיחה (נר פנימי)",
    "puzzles.puzzle.p-trend-2.title": "מגמת ירידה או צד?",
    "puzzles.puzzle.p-trend-2.prompt": "קבע את המגמה הדומיננטית. השתמש בכל רצף הנרות וגבה בשני ראיות לפחות.",
    "puzzles.puzzle.p-trend-2.explanation": "כל שיא נמוך מהקודם וכל מדף נמוך מהקודם. המבנה מגדיר מגמת ירידה.",
    "puzzles.puzzle.p-zone-1.title": "סמן את אזור התמיכה",
    "puzzles.puzzle.p-zone-1.prompt": "מצא את אזור התמיכה המדויק שבו קונים נכנסו. תן טווח מחירים וחלון זמן שבו המחיר clearly held.",
    "puzzles.puzzle.p-zone-1.explanation": "המחיר מצא תמיכה בטווח 192–199 לפני הקפיצה. באזור הזה נבלעה המכירה והופיעו קונים.",
    "puzzles.puzzle.p-risk-1.title": "הגדר כניסה, stop ויעד",
    "puzzles.puzzle.p-risk-1.prompt": "הצב את העסקה: כניסה, stop מתחת ל־swing low האחרון (תמיכה), ויעד. היעד חייב להציע לפחות 1:1 risk–reward.",
    "puzzles.puzzle.p-risk-1.explanation": "כניסה ליד הסגירה האחרונה, stop מתחת ל־low האחרון, ויעד לפחות 1:1.",
    "puzzles.puzzle.p-mcq-2.title": "מה קרה אחר כך? (מבנה לא חלק)",
    "puzzles.puzzle.p-mcq-2.prompt": "המחיר עצר אחרי העלייה. איזו תוצאה היא הפתרון האמיתי—פריצה או כישלון? השתמש גם במגמה וגם בטווח האחרון.",
    "puzzles.puzzle.p-mcq-2.explanation": "המגמה הקודמת הייתה עלייה והמחיר מתקבץ ליד השיאים. הפתרון העקבי ביותר הוא פריצה מעל הטווח.",
    "puzzles.puzzle.p-mcq-2.option.0": "המחיר פרץ מתחת לטווח וירד חדות",
    "puzzles.puzzle.p-mcq-2.option.1": "המחיר פרץ מעל הטווח ועשה שיא חדש",
    "puzzles.puzzle.p-mcq-2.option.2": "המחיר נשאר בתוך הטווח בנר נוסף",
    "puzzles.puzzle.p-mcq-2.option.3": "המחיר קפץ למעלה ואז מילא את הפער",
    "puzzles.puzzle.p-mcq-2.option.4": "המחיר בחן את תחתית הטווח ואז קפץ חזרה פנימה",
    "puzzles.puzzle.p-trend-3.title": "מגמה בגרף לא חלק",
    "puzzles.puzzle.p-trend-3.prompt": "גרף רועש. קבע את המגמה הדומיננטית מהנר הראשון לאחרון. השתמש בשני ראיות לפחות—מבנה וגופים, לא פתילים בודדים.",
    "puzzles.puzzle.p-trend-3.explanation": "למרות נר הירידה ביום 2, רצף השיאים והמדפים מראה שיאים עולים ומדפים עולים. המגמה הדומיננטית היא עלייה.",
    "puzzles.title": "חידות",
    "puzzles.description": "אתגרי קריאת גרפים. בנה דירוג ורצפים. חינוך בלבד—לא ייעוץ מסחר.",
    "puzzles.dailyPuzzle": "חידון יומי",
    "puzzles.completedToday": "הושלם היום",
    "puzzles.oneNewDaily": "חידון חדש כל יום",
    "puzzles.playAgain": "שחק שוב",
    "puzzles.playTodays": "שחק את חידון היום",
    "puzzles.load": "טוען…",
    "puzzles.reviewTodays": "סקור את חידון היום",
    "puzzles.streak": "רצף",
    "puzzles.daysInRow": "ימים ברצף",
    "puzzles.rating": "דירוג",
    "puzzles.ratingDesc": "דירוג חידות (סגנון Elo)",
    "puzzles.couldNotGetSession": "לא ניתן לקבל הפעלה. נסה שוב.",
    "puzzles.sessionExpiredProgress": "ההפעלה פגה. התחבר שוב לראות התקדמות ושחק לאחרונה.",
    "puzzles.somethingWentWrong": "משהו השתבש בטעינת ההתקדמות.",
    "puzzles.recentlyPlayed": "שוחק לאחרונה",
    "puzzles.jumpBackReview": "חזור או סקור טעויות",
    "puzzles.practice": "תרגול",
    "puzzles.filterAndSort": "סנן ומיין חידות",
    "puzzles.puzzleSets": "סטים של חידות",
    "puzzles.mistakeReview": "סקירת טעויות",
    "puzzles.recommendedForYou": "מומלץ עבורך",
    "puzzles.allPuzzles": "כל החידות",
    "puzzles.noPuzzlesMatch": "אין חידות התואמות את המסננים.",
    "puzzles.educationDisclaimer": "חידות לחינוך בלבד. לא ייעוץ פיננסי או מסחר. אין כסף אמיתי בסיכון.",
    "leaderboard.title": "לוח מובילים עולמי",
    "leaderboard.description": "לומדים מובילים לפי XP. השלם שיעורים וחידות כדי לטפס.",
    "leaderboard.yourProgress": "ההתקדמות שלך",
    "leaderboard.keepGoing": "המשך כדי להגיע ל־20 הראשונים!",
    "leaderboard.top20": "Top 20 לפי XP",
    "leaderboard.rankDescription": "דירוג, שם תצוגה ונקודות ניסיון",
    "leaderboard.emptyMessage": "אין עדיין רשומות. השלם שיעורים וחידות כדי להרוויח XP ולהופיע בלוח!",
    "achievements.title": "הישגים",
    "achievements.subtitle": "האוסף וההתקדמות שלך",
    "achievements.allRarity": "כל הנדירות",
    "achievements.allCategories": "כל הקטגוריות",
    "achievements.searchPlaceholder": "חפש הישגים…",
    "achievements.empty": "אין הישגים התואמים את המסננים.",
    "achievements.unlocked": "נפתח",
    "achievements.locked": "נעול",
    "achievements.collectAndClimb": "אסוף הישגים וטפס בדירוג",
    "achievements.totalXp": "XP סה״כ",
    "achievements.completion": "השלמה",
    "achievements.rarityBreakdown": "פירוט נדירות",
    "achievements.noFound": "לא נמצאו הישגים",
    "achievements.tryAdjustingFilters": "נסה לשנות את המסננים",
    "achievements.noUnlockedYet": "עדיין לא נפתחו הישגים",
    "achievements.startCompeting": "התחל להתחרות בקבוצות כדי לפתוח הישגים!",
    "leagues.loading": "טוען ליגות…",
    "leagues.backToDashboard": "חזרה ללוח הבקרה",
    "leagues.noLeaguesYet": "אין עדיין ליגות",
    "leagues.createFirstLeague": "צור את הליגה הראשונה שלך כדי להתחיל להתחרות עם חברים",
    "alerts.title": "התראות והגדרות",
    "alerts.subtitle": "הגדר התראות מחיר ועקוב אחר פעילות השוק",
    "alerts.activeAlerts": "התראות פעילות",
    "alerts.triggeredToday": "הופעלו היום",
    "alerts.upcomingEarnings": "רווחים קרובים",
    "alerts.unusualActivity": "פעילות יוצאת דופן",
    "leagues.title": "ליגות",
    "leagues.subtitle": "התחרה עם חברים ועקוב אחר ביצועים",
    "leagues.createLeague": "צור ליגה",
    "leagues.myLeagues": "הליגות שלי",
    "leagues.discover": "גלה",
    "leagues.leagueName": "שם הליגה",
    "leagues.leagueDescription": "תיאור (אופציונלי)",
    "leagues.create": "צור",
    "leagues.leagueCreated": "הליגה נוצרה!",
    "leagues.inviteLink": "קישור הזמנה",
    "leagues.noLeagues": "אתה עדיין לא בשום ליגה.",
    "leagues.joinOrCreate": "הצטרף עם קישור הזמנה או צור ליגה.",
    "leagues.startNewLeague": "התחל ליגת תחרות חדשה",
    "transactions.title": "עסקאות",
    "transactions.subtitle": "צפה וסנן את עסקאות התיק",
    "risk.title": "סיכון וביצועים",
    "risk.subtitle": "ניתוח מדדי סיכון וביצועי בנצ'מרק",
    "tax.title": "דיווח מס",
    "tax.subtitle": "מעקב רווחים ממומשים ולא ממומשים עם FIFO/LIFO",
    "dividends.title": "מעקב דיבידנדים",
    "dividends.subtitle": "מעקב תשלומי דיבידנד, תשואות ותאריכי אקס-דיבידנד",
    "ratings.title": "דירוגי אנליסטים",
    "ratings.subtitle": "המלצות אנליסטים מקצועיות לנכסים שלך",
    "validated.title": "בחירות מאומתות",
    "validated.pageTitle": "המלצות מאומתות אנליסט",
    "validated.pageSubtitle": "המלצות השקעה מבוססות כללים עם אלגוריתם אימות קפדני",
    "transparent.title": "ניתוח שקוף",
    "transparent.pageTitle": "ניתוח השקעות שקוף",
    "transparent.subtitle": "ניתוח רב-מקורות מקיף בשקיפות מלאה למתודולוגיה",
    "aiAssistant.title": "עוזר AI",
    "aiAssistant.subtitle": "קבל תשובות בזמן אמת על התיק, פרטי החשבון ואסטרטגיות השקעה",
    "aiAssistant.loading": "טוען עוזר AI...",
    "investSmarter.title": "השקע חכם",
    "investSmarter.twelveStepSystem": "מערכת 12 השלבים",
    "decisions.title": "החלטות",
    "decisions.pageTitle": "תמיכה בהחלטות",
    "decisions.subtitle": "המלצות וניתוח תרחישים מבוססי AI",
    "decisions.buySignals": "איתותי קנייה",
    "decisions.sellSignals": "איתותי מכירה",
    "decisions.rebalance": "איזון מחדש",
    "decisions.opportunities": "הזדמנויות",
    "visualizations.title": "תצוגות",
    "visualizations.pageTitle": "תצוגות אינטראקטיביות",
    "visualizations.subtitle": "חקור את התיק עם גרפים וכלים אינטראקטיביים",
    "visualizations.loading": "טוען תצוגות...",
    "dashboard.overview": "סקירת תיק",
    "dashboard.overviewSubtitle": "עקוב אחר ההשקעות והביצועים שלך",
    "dashboard.noHoldings": "אין עדיין נכסים",
    "dashboard.noHoldingsDesc": "התחל לבנות את התיק על ידי הוספת הנכס הראשון. בחר מניות, ETF או קריפטו.",
    "dashboard.addHoldings": "הוסף נכסים",
    "dashboard.addHolding": "הוסף נכס",
    "dashboard.setupOnboarding": "הגדר דרך ההתחלה",
    "dashboard.or": "או",
    "dashboard.stocks": "מניות",
    "dashboard.etfs": "ETF",
    "dashboard.crypto": "קריפטו",
    "dashboard.showValues": "הצג ערכים",
    "dashboard.totalPortfolio": "סה״כ תיק",
    "dashboard.perAssetView": "תצוגה לנכס",
    "auth.signInFailed": "ההתחברות נכשלה",
    "auth.failedGoogleSignIn": "ההתחברות עם Google נכשלה",
    "auth.missingFields": "שדות חסרים",
    "auth.enterEmailPassword": "נא להזין אימייל וסיסמה",
    "auth.useGoogleSignIn": "התחבר עם Google",
    "auth.googleAccountHint": "חשבון זה נוצר עם Google. התחבר עם Google לגישה, או צור סיסמה בהגדרות.",
    "auth.wrongPassword": "אימייל או סיסמה שגויים.",
    "auth.tooManyAttempts": "יותר מדי ניסיונות",
    "auth.tryAgainLater": "נסה שוב מאוחר יותר או אפס סיסמה.",
    "auth.failedAddTransaction": "הוספת העסקה נכשלה",
    "auth.signInToAdd": "התחבר כדי להוסיף עסקאות",
    "auth.fillRequired": "מלא את כל השדות הנדרשים",
    "auth.sharesCostPositive": "מניות ומחיר למניה חייבים להיות גדולים מ-0",
    "profile.title": "פרופיל",
    "admin.title": "מנהל",
    "dashboard.title": "לוח בקרה",
    "join.title": "הצטרף לליגה",
    "join.acceptInvite": "קבל הזמנה",
    "join.invalidInvite": "הזמנה לא תקינה או שפגה",
    "join.invalidLink": "קישור הזמנה לא תקין",
    "auth.emailPlaceholder": "הזן את כתובת האימייל שלך",
    "auth.passwordPlaceholder": "הזן את הסיסמה שלך",
    "auth.signInSubHeader": "התחבר עם אימייל וסיסמה, או השתמש ב-Google",
    "transaction.title": "הוסף עסקה",
    "transaction.description": "קנה או מכור מניות, ETF או קריפטו",
    "transaction.type": "סוג עסקה",
    "transaction.buy": "קנייה",
    "transaction.sell": "מכירה",
    "transaction.symbol": "סימול *",
    "transaction.symbolPlaceholder": "AAPL, BTC, SPYM",
    "transaction.shares": "מניות *",
    "transaction.sharesPlaceholder": "10",
    "transaction.costPerShare": "מחיר למניה *",
    "transaction.costPlaceholder": "150.00",
    "transaction.date": "תאריך *",
    "transaction.commission": "עמלת פלטפורמה (%)",
    "transaction.commissionPlaceholder": "0",
    "transaction.commissionHintBuy": "העמלה מתווספת לערך העסקה (מגדילה בסיס עלות).",
    "transaction.commissionHintSell": "העמלה מופחתת proceeds המכירה.",
    "transaction.commissionEg": "למשל 2 עבור 2%.",
    "transaction.commissionType": "סוג עמלה",
    "transaction.commissionPercent": "אחוז",
    "transaction.commissionFlat": "שער קבוע",
    "transaction.commissionPercentLabel": "עמלה (%)",
    "transaction.commissionFlatLabel": "עמלה ($)",
    "transaction.commissionFlatPlaceholder": "0.00",
    "transaction.commissionFlatHint": "עמלה קבועה בדולרים לעסקה (למשל 5.95).",
    "transaction.notes": "הערות (אופציונלי)",
    "transaction.notesPlaceholder": "הוסף הערות על העסקה",
    "transaction.add": "הוסף עסקה",
    "transaction.adding": "מוסיף...",
    "transaction.added": "העסקה נוספה",
    "transaction.invalidValues": "ערכים לא תקינים",
    "transaction.notAuth": "לא מאומת",
    "transaction.cannotSell": "לא ניתן למכור",
    "transaction.dontOwn": "אין לך",
    "transaction.insufficientShares": "אין מספיק מניות",
    "transaction.onlyOwn": "יש לך רק",
    "transaction.sharesOf": "מניות של",
    "transaction.at": "ב",
    "asset.title": "הוסף נכס",
    "asset.description": "בחר מניות, ETF או קריפטו, חפש ובחר נכס.",
    "asset.whatToAdd": "מה תרצה להוסיף?",
    "asset.searchStocks": "חפש מניות",
    "asset.searchEtfs": "חפש ETF",
    "asset.searchCrypto": "חפש קריפטו",
    "asset.placeholderStocks": "למשל GOOGL, AAPL, MSFT",
    "asset.placeholderEtfs": "למשל SPY, QQQ, VTI",
    "asset.placeholderCrypto": "למשל Bitcoin, ETH, SOL",
    "asset.searching": "מחפש…",
    "asset.loadingPrices": "טוען מחירים…",
    "asset.quantity": "כמות",
    "asset.buyPrice": "מחיר קנייה (ליחידה)",
    "asset.buyDate": "תאריך קנייה (אופציונלי)",
    "asset.commissionHint": "אם שילמת יותר מערך המניות, הזן אחוז עמלה (למשל 2).",
    "asset.summary": "סיכום",
    "asset.costBasis": "בסיס עלות",
    "asset.currentValue": "ערך נוכחי",
    "asset.unrealizedPnl": "רווח/הפסד לא ממומש ($)",
    "asset.unrealizedPnlPercent": "רווח/הפסד לא ממומש (%)",
    "asset.confirmSave": "אישור ושמירה",
    "asset.saving": "שומר…",
    "asset.change": "החלף",
    "asset.added": "הנכס נוסף",
    "asset.addedDesc": "נוסף לתיק.",
    "asset.failed": "הוספת הנכס נכשלה",
    "alerts.yourAlerts": "ההתראות שלך",
    "alerts.noAlerts": "אין התראות פעילות",
    "alerts.noAlertsHint": "צור התראה לקבלת עדכונים באימייל",
    "alerts.alertDeleted": "ההתראה נמחקה",
    "alerts.alertRemoved": "ההתראה הוסרה.",
    "alerts.failedDelete": "מחיקת ההתראה נכשלה",
    "alerts.createNew": "צור התראה חדשה",
    "alerts.alertType": "סוג התראה",
    "alerts.targetPrice": "מחיר יעד",
    "alerts.ratingChange": "שינוי דירוג",
    "alerts.majorNews": "חדשות מרכזיות",
    "alerts.earnings": "דוחות רווח",
    "alerts.symbol": "סימול",
    "alerts.select": "בחר...",
    "alerts.condition": "תנאי",
    "alerts.value": "ערך",
    "alerts.enterPrice": "הזן מחיר",
    "alerts.createAlert": "צור התראה",
    "alerts.changeInSettings": "(שינוי בהגדרות)",
    "alerts.emailNotifications": "התראות אימייל נשלחות ל",
    "alerts.missingInfo": "חסר מידע",
    "alerts.missingInfoDesc": "בחר סימול וודא שהאימייל מוגדר.",
    "alerts.missingPrice": "חסר מחיר",
    "alerts.missingPriceDesc": "הזן ערך מחיר יעד.",
    "alerts.alertCreated": "ההתראה נוצרה",
    "alerts.alertCreatedDesc": "תקבל התראות אימייל כשהתנאי מתקיים.",
    "alerts.failedCreate": "יצירת ההתראה נכשלה",
    "alerts.failedCreateDesc": "יצירת ההתראה נכשלה. נסה שוב.",
    "alerts.triggered": "הופעל",
    "alerts.ratingChangeLabel": "שינוי דירוג",
    "alerts.majorNewsLabel": "חדשות מרכזיות",
    "alerts.earningsLabel": "דוחות רווח",
    "alerts.goesAbove": "עולה מעל",
    "alerts.goesBelow": "יורד מתחת",
    "alerts.changesByPercent": "שינוי באחוזים",
    "alerts.creating": "יוצר...",
    "alerts.sentTo": "התראות יישלחו ל:",
    "league.today": "היום",
    "league.week": "שבוע",
    "league.month": "חודש",
    "league.year": "שנה",
    "league.dailySprint": "ספרינט יומי",
    "league.weeklyRound": "סיבוב שבועי",
    "league.monthlyStandings": "דירוג חודשי",
    "league.season": "עונה",
    "league.noMembersYet": "אין עדיין חברים.",
    "league.standingsLoading": "טוען דירוג.",
    "league.leading": "מוביל",
    "league.copied": "הועתק ללוח",
    "league.couldNotCreateInvite": "לא ניתן ליצור הזמנה",
    "league.failedCreateInvite": "יצירת קישור הזמנה נכשלה",
    "league.updatedJustNow": "עודכן עכשיו",
    "league.updated1mAgo": "עודכן לפני דקה",
    "league.member": "חבר",
    "league.owner": "בעלים",
    "league.admin": "מנהל",
    "league.league": "ליגה",
    "admin.subtitle": "סקירת פלטפורמה וניהול משתמשים",
    "admin.accessDenied": "גישה נדחתה",
    "admin.accessDeniedDesc": "נדרשת גישת מנהל לצפייה בדף זה.",
    "admin.loading": "טוען לוח מנהלים ומחירים...",
    "admin.dashboardTitle": "לוח מנהלים",
    "admin.accessRequired": "נדרשת גישת מנהל",
    "admin.totalUsers": "סה״כ משתמשים",
    "admin.platformValue": "ערך פלטפורמה",
    "admin.totalPnL": "סה״כ רווח/הפסד",
    "admin.totalHoldings": "סה״כ נכסים",
    "admin.totalTransactions": "סה״כ עסקאות",
    "admin.cannotModifySelf": "לא ניתן לשנות את סטטוס המנהל שלך",
    "admin.cannotModifySelfDesc": "לא ניתן לשנות את סטטוס המנהל שלך",
    "admin.adminGranted": "סטטוס מנהל הוענק",
    "admin.adminRemoved": "סטטוס מנהל הוסר",
    "admin.isNowAdmin": "כעת מנהל",
    "admin.noLongerAdmin": "אינו עוד מנהל",
    "admin.failedUpdateAdmin": "עדכון סטטוס המנהל נכשל",
    "profile.loading": "טוען פרופיל…",
    "profile.userNotFound": "משתמש לא נמצא",
    "profile.failedLoad": "טעינת הפרופיל נכשלה",
    "profile.backToLeagues": "חזרה לליגות",
    "profile.performanceAllocation": "ביצועים והקצאה (אחוזים בלבד)",
    "profile.performance": "ביצועים",
    "profile.holdingsAllocation": "הקצאת נכסים",
    "profile.percentBySymbol": "אחוז מתיק לפי סימול. ללא ערכים בדולר.",
    "profile.noHoldingsData": "אין נתוני נכסים או הקצאה.",
  },
}

const LOCALE_STORAGE_KEY = "app_locale"

export function getStoredLocale(): Locale | null {
  if (typeof window === "undefined") return null
  const v = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (v === "he" || v === "en") return v
  return null
}

export function setStoredLocale(locale: Locale): void {
  if (typeof window === "undefined") return
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
}

export function getMessage(locale: Locale, key: string): string {
  const map = translations[locale]
  const value = map?.[key]
  if (value != null && value !== "") return value
  const enMap = translations.en
  return (enMap?.[key] as string) ?? key
}
